/**
 * React Query Hook: Create Session For Guest
 *
 * Hook for creating a session from unauthenticated user migration.
 * This handles both session creation and credit deduction in one atomic operation.
 * Endpoint: POST /api/sessions/create-for-guest
 */

import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { createSessionForGuest } from "@/utils/multi-session";
import type { Database } from "@/integrations/supabase/types";

type FormSession = Database["public"]["Tables"]["form_sessions"]["Row"];

export interface CreateSessionForGuestResponse {
  session: FormSession;
  credits_deducted: boolean;
  insufficient_credits?: boolean;
  shortage?: number;
  current_balance?: number;
  required?: number;
  new_balance?: number;
}

/**
 * Hook to create a session for guest migration
 *
 * Features:
 * - Creates session at step 2 or 3 depending on credit availability
 * - Handles credit deduction automatically on backend
 * - If credits available: Creates session at step 3
 * - If insufficient credits: Creates session at step 2
 * - Provides loading and error states
 *
 * @returns React Query mutation result
 */
export function useCreateSessionForGuest(): UseMutationResult<
  CreateSessionForGuestResponse,
  Error,
  { isFromLandingPage: boolean; cui?: string },
  unknown
> {
  return useMutation({
    mutationFn: async ({ isFromLandingPage, cui }) => {
      const result = await createSessionForGuest(isFromLandingPage, cui);

      if (!result) {
        throw new Error("Failed to create session - user not authenticated");
      }

      return result;
    },
  });
}
